import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  Car,
  Clapperboard,
  Heart,
  Home,
  Laptop,
  Plane,
  ShoppingBag,
  UtensilsCrossed,
  Wrench,
} from 'lucide-react';

export interface MccSubcategory {
  code: string;
  label_en: string;
  label_es: string;
}

export interface MccCategory {
  id: string;
  icon: LucideIcon;
  subcategories: MccSubcategory[];
}

export const MCC_CATEGORIES: MccCategory[] = [
  {
    id: 'food',
    icon: UtensilsCrossed,
    subcategories: [
      {
        code: '5411',
        label_en: 'Grocery stores & supermarkets',
        label_es: 'Supermercados y tiendas de comestibles',
      },
      {
        code: '5422',
        label_en: 'Meat provisioners & freezer',
        label_es: 'Proveedores de carne y congelados',
      },
      {
        code: '5441',
        label_en: 'Candy, nut & confectionery stores',
        label_es: 'Dulcerías y confiterías',
      },
      {
        code: '5451',
        label_en: 'Dairy products stores',
        label_es: 'Tiendas de lácteos',
      },
      { code: '5462', label_en: 'Bakeries', label_es: 'Panaderías' },
      {
        code: '5499',
        label_en: 'Misc food stores & convenience',
        label_es: 'Tiendas de conveniencia',
      },
      { code: '5812', label_en: 'Restaurants', label_es: 'Restaurantes' },
      {
        code: '5813',
        label_en: 'Bars, cocktail lounges & nightclubs',
        label_es: 'Bares y clubes nocturnos',
      },
      {
        code: '5814',
        label_en: 'Fast food restaurants',
        label_es: 'Comida rápida',
      },
    ],
  },
  {
    id: 'transport',
    icon: Car,
    subcategories: [
      {
        code: '4111',
        label_en: 'Local commuter transport',
        label_es: 'Transporte público local',
      },
      {
        code: '4112',
        label_en: 'Passenger railways',
        label_es: 'Ferrocarriles de pasajeros',
      },
      {
        code: '4121',
        label_en: 'Taxis & rideshares',
        label_es: 'Taxis y apps de transporte',
      },
      { code: '4131', label_en: 'Bus lines', label_es: 'Líneas de autobuses' },
      {
        code: '4214',
        label_en: 'Motor freight & moving',
        label_es: 'Mudanzas y carga',
      },
      {
        code: '4789',
        label_en: 'Transportation services (other)',
        label_es: 'Otros servicios de transporte',
      },
      {
        code: '5013',
        label_en: 'Motor vehicle supplies & parts',
        label_es: 'Repuestos y accesorios de vehículos',
      },
      {
        code: '5511',
        label_en: 'Car & truck dealers (new & used)',
        label_es: 'Concesionarios de autos',
      },
      {
        code: '5521',
        label_en: 'Car & truck dealers (used only)',
        label_es: 'Autos usados',
      },
      {
        code: '5531',
        label_en: 'Auto & home supply stores',
        label_es: 'Tiendas de autopartes',
      },
      {
        code: '5532',
        label_en: 'Automotive tire stores',
        label_es: 'Tiendas de llantas',
      },
      {
        code: '5533',
        label_en: 'Automotive parts & accessories',
        label_es: 'Partes y accesorios automotriz',
      },
      {
        code: '5541',
        label_en: 'Gas stations',
        label_es: 'Estaciones de gasolina',
      },
      {
        code: '5542',
        label_en: 'Fuel dealers (non-auto)',
        label_es: 'Distribuidores de combustible',
      },
      {
        code: '5571',
        label_en: 'Motorcycle shops & dealers',
        label_es: 'Tiendas de motocicletas',
      },
      {
        code: '7512',
        label_en: 'Car rental agencies',
        label_es: 'Agencias de renta de autos',
      },
      {
        code: '7523',
        label_en: 'Parking lots & garages',
        label_es: 'Estacionamientos',
      },
      {
        code: '7531',
        label_en: 'Automotive body repair',
        label_es: 'Talleres de carrocería',
      },
      {
        code: '7534',
        label_en: 'Tire retreading & repair',
        label_es: 'Reparación de llantas',
      },
      {
        code: '7535',
        label_en: 'Auto paint shops',
        label_es: 'Talleres de pintura automotriz',
      },
      {
        code: '7538',
        label_en: 'Auto service shops',
        label_es: 'Talleres mecánicos',
      },
      { code: '7542', label_en: 'Car washes', label_es: 'Lavado de autos' },
      {
        code: '7549',
        label_en: 'Towing services',
        label_es: 'Servicio de grúas',
      },
    ],
  },
  {
    id: 'travel',
    icon: Plane,
    subcategories: [
      { code: '4411', label_en: 'Cruise lines', label_es: 'Cruceros' },
      {
        code: '4511',
        label_en: 'Airlines & air carriers',
        label_es: 'Aerolíneas',
      },
      {
        code: '4722',
        label_en: 'Travel agencies & tour operators',
        label_es: 'Agencias de viaje',
      },
      {
        code: '7011',
        label_en: 'Hotels, motels & resorts',
        label_es: 'Hoteles y resorts',
      },
      { code: '7012', label_en: 'Timeshares', label_es: 'Tiempos compartidos' },
      {
        code: '7032',
        label_en: 'Recreational & sporting camps',
        label_es: 'Campamentos recreativos',
      },
      {
        code: '7033',
        label_en: 'Trailer parks & campgrounds',
        label_es: 'Parques de remolques y camping',
      },
    ],
  },
  {
    id: 'entertainment',
    icon: Clapperboard,
    subcategories: [
      {
        code: '5735',
        label_en: 'Record stores',
        label_es: 'Tiendas de música',
      },
      {
        code: '5815',
        label_en: 'Digital goods – media',
        label_es: 'Contenido digital y medios',
      },
      {
        code: '5816',
        label_en: 'Digital goods – games',
        label_es: 'Juegos digitales',
      },
      {
        code: '5817',
        label_en: 'Digital goods – applications',
        label_es: 'Aplicaciones digitales',
      },
      {
        code: '5818',
        label_en: 'Digital goods – large marketplace',
        label_es: 'Marketplace digital',
      },
      { code: '7832', label_en: 'Movie theaters', label_es: 'Cines' },
      {
        code: '7841',
        label_en: 'Video/DVD rental & streaming',
        label_es: 'Streaming y alquiler de video',
      },
      {
        code: '7911',
        label_en: 'Entertainment events & concerts',
        label_es: 'Eventos y conciertos',
      },
      {
        code: '7922',
        label_en: 'Theatrical producers & ticket agencies',
        label_es: 'Productoras y taquillas',
      },
      {
        code: '7929',
        label_en: 'Bands, orchestras & entertainers',
        label_es: 'Bandas y artistas',
      },
      {
        code: '7932',
        label_en: 'Billiard & pool establishments',
        label_es: 'Billares',
      },
      { code: '7933', label_en: 'Bowling alleys', label_es: 'Boliches' },
      {
        code: '7941',
        label_en: 'Sports clubs & fields',
        label_es: 'Clubes deportivos',
      },
      {
        code: '7991',
        label_en: 'Tourist attractions & exhibits',
        label_es: 'Atracciones turísticas',
      },
      {
        code: '7993',
        label_en: 'Video game arcades',
        label_es: 'Arcades de videojuegos',
      },
      {
        code: '7994',
        label_en: 'Video game arcades/establishments',
        label_es: 'Establecimientos de videojuegos',
      },
      {
        code: '7996',
        label_en: 'Amusement parks & theme parks',
        label_es: 'Parques de diversiones',
      },
      {
        code: '7998',
        label_en: 'Aquariums & zoos',
        label_es: 'Acuarios y zoológicos',
      },
      {
        code: '7999',
        label_en: 'Recreation services (other)',
        label_es: 'Otros servicios recreativos',
      },
    ],
  },
  {
    id: 'health',
    icon: Heart,
    subcategories: [
      {
        code: '5912',
        label_en: 'Pharmacies & drug stores',
        label_es: 'Farmacias y droguerías',
      },
      {
        code: '5975',
        label_en: 'Hearing aids, sales & service',
        label_es: 'Audífonos y servicios',
      },
      {
        code: '5976',
        label_en: 'Orthopedic goods & prosthetics',
        label_es: 'Ortopedia y prótesis',
      },
      {
        code: '7297',
        label_en: 'Massage parlors',
        label_es: 'Salones de masajes',
      },
      {
        code: '7298',
        label_en: 'Health & beauty spas',
        label_es: 'Spas de salud y belleza',
      },
      { code: '8011', label_en: 'Doctors & physicians', label_es: 'Médicos' },
      {
        code: '8021',
        label_en: 'Dentists & orthodontists',
        label_es: 'Dentistas y ortodoncistas',
      },
      {
        code: '8031',
        label_en: 'Osteopathic physicians',
        label_es: 'Médicos osteópatas',
      },
      { code: '8041', label_en: 'Chiropractors', label_es: 'Quiroprácticos' },
      {
        code: '8042',
        label_en: 'Optometrists & ophthalmologists',
        label_es: 'Optometristas y oftalmólogos',
      },
      {
        code: '8043',
        label_en: 'Opticians & optical goods',
        label_es: 'Ópticas',
      },
      {
        code: '8049',
        label_en: 'Podiatrists & chiropodists',
        label_es: 'Podólogos',
      },
      {
        code: '8050',
        label_en: 'Nursing & personal care facilities',
        label_es: 'Centros de cuidado personal',
      },
      { code: '8062', label_en: 'Hospitals', label_es: 'Hospitales' },
      {
        code: '8071',
        label_en: 'Medical & dental labs',
        label_es: 'Laboratorios médicos y dentales',
      },
      {
        code: '8099',
        label_en: 'Medical services (other)',
        label_es: 'Otros servicios médicos',
      },
    ],
  },
  {
    id: 'shopping',
    icon: ShoppingBag,
    subcategories: [
      {
        code: '5200',
        label_en: 'Home supply & warehouse stores',
        label_es: 'Tiendas de hogar y almacenes',
      },
      {
        code: '5311',
        label_en: 'Department stores',
        label_es: 'Tiendas por departamento',
      },
      {
        code: '5331',
        label_en: 'Variety stores',
        label_es: 'Tiendas de variedades',
      },
      {
        code: '5399',
        label_en: 'General merchandise',
        label_es: 'Mercancía general',
      },
      {
        code: '5611',
        label_en: "Men's & boy's clothing stores",
        label_es: 'Tiendas de ropa para hombre',
      },
      {
        code: '5621',
        label_en: "Women's clothing stores",
        label_es: 'Tiendas de ropa para mujer',
      },
      {
        code: '5631',
        label_en: "Women's accessories stores",
        label_es: 'Accesorios para mujer',
      },
      {
        code: '5641',
        label_en: "Children's & infant's clothing",
        label_es: 'Ropa infantil',
      },
      {
        code: '5651',
        label_en: 'Family clothing stores',
        label_es: 'Tiendas de ropa familiar',
      },
      { code: '5661', label_en: 'Shoe stores', label_es: 'Zapaterías' },
      {
        code: '5691',
        label_en: 'Clothing accessories',
        label_es: 'Accesorios de ropa',
      },
      {
        code: '5699',
        label_en: 'Misc apparel & accessory shops',
        label_es: 'Otras tiendas de ropa',
      },
      {
        code: '5944',
        label_en: 'Jewelry, watches & silverware',
        label_es: 'Joyerías y relojerías',
      },
      {
        code: '5945',
        label_en: 'Hobby, toy & game shops',
        label_es: 'Jugueterías y hobbies',
      },
      {
        code: '5947',
        label_en: 'Gift, card & novelty stores',
        label_es: 'Tiendas de regalos',
      },
      {
        code: '5948',
        label_en: 'Luggage & leather goods',
        label_es: 'Equipaje y artículos de cuero',
      },
    ],
  },
  {
    id: 'technology',
    icon: Laptop,
    subcategories: [
      {
        code: '5045',
        label_en: 'Computers & peripherals',
        label_es: 'Computadoras y periféricos',
      },
      {
        code: '5065',
        label_en: 'Electronic parts & equipment',
        label_es: 'Partes electrónicas',
      },
      {
        code: '5732',
        label_en: 'Electronics stores',
        label_es: 'Tiendas de electrónica',
      },
      {
        code: '5733',
        label_en: 'Music stores & instruments',
        label_es: 'Tiendas de música e instrumentos',
      },
      {
        code: '5734',
        label_en: 'Computer software stores',
        label_es: 'Tiendas de software',
      },
      {
        code: '5946',
        label_en: 'Camera & photographic stores',
        label_es: 'Tiendas de cámaras y fotografía',
      },
      {
        code: '7372',
        label_en: 'Computer programming & data processing',
        label_es: 'Programación y procesamiento de datos',
      },
      {
        code: '7379',
        label_en: 'Computer maintenance & repair',
        label_es: 'Mantenimiento y reparación de computadoras',
      },
    ],
  },
  {
    id: 'education',
    icon: BookOpen,
    subcategories: [
      {
        code: '5111',
        label_en: 'Stationery & office supplies',
        label_es: 'Papelería y artículos de oficina',
      },
      {
        code: '5192',
        label_en: 'Books, periodicals & newspapers',
        label_es: 'Libros, revistas y periódicos',
      },
      { code: '5942', label_en: 'Book stores', label_es: 'Librerías' },
      { code: '5943', label_en: 'Stationery stores', label_es: 'Papelerías' },
      {
        code: '8211',
        label_en: 'Elementary & secondary schools',
        label_es: 'Escuelas primarias y secundarias',
      },
      {
        code: '8220',
        label_en: 'Colleges & universities',
        label_es: 'Universidades',
      },
      {
        code: '8241',
        label_en: 'Correspondence schools',
        label_es: 'Escuelas por correspondencia',
      },
      {
        code: '8244',
        label_en: 'Business & secretarial schools',
        label_es: 'Escuelas de negocios',
      },
      {
        code: '8249',
        label_en: 'Vocational & trade schools',
        label_es: 'Escuelas vocacionales',
      },
      {
        code: '8299',
        label_en: 'Schools & educational services (other)',
        label_es: 'Otros servicios educativos',
      },
    ],
  },
  {
    id: 'home',
    icon: Home,
    subcategories: [
      {
        code: '5021',
        label_en: 'Office & commercial furniture',
        label_es: 'Muebles de oficina',
      },
      {
        code: '5039',
        label_en: 'Construction materials',
        label_es: 'Materiales de construcción',
      },
      {
        code: '5211',
        label_en: 'Lumber & building materials',
        label_es: 'Madera y materiales de construcción',
      },
      {
        code: '5231',
        label_en: 'Glass, paint & wallpaper stores',
        label_es: 'Vidrios, pintura y papel tapiz',
      },
      { code: '5251', label_en: 'Hardware stores', label_es: 'Ferreterías' },
      {
        code: '5261',
        label_en: 'Nurseries & lawn/garden supply',
        label_es: 'Viveros y jardinería',
      },
      {
        code: '5712',
        label_en: 'Furniture & home furnishings',
        label_es: 'Muebles y decoración del hogar',
      },
      {
        code: '5713',
        label_en: 'Floor covering stores',
        label_es: 'Tiendas de pisos',
      },
      {
        code: '5714',
        label_en: 'Drapery & upholstery stores',
        label_es: 'Cortinas y tapicería',
      },
      {
        code: '5718',
        label_en: 'Fireplace & accessories stores',
        label_es: 'Tiendas de chimeneas',
      },
      {
        code: '5719',
        label_en: 'Misc home furnishings',
        label_es: 'Otros artículos para el hogar',
      },
      {
        code: '5722',
        label_en: 'Household appliance stores',
        label_es: 'Tiendas de electrodomésticos',
      },
      {
        code: '4900',
        label_en: 'Utilities – electric, gas, water',
        label_es: 'Servicios públicos',
      },
      {
        code: '4814',
        label_en: 'Telecommunications',
        label_es: 'Telecomunicaciones',
      },
      {
        code: '4816',
        label_en: 'Computer network & info services',
        label_es: 'Servicios de red e información',
      },
      {
        code: '4899',
        label_en: 'Cable & pay TV services',
        label_es: 'Servicios de cable y TV',
      },
    ],
  },
  {
    id: 'services',
    icon: Wrench,
    subcategories: [
      {
        code: '7210',
        label_en: 'Laundry & cleaning services',
        label_es: 'Lavandería y limpieza',
      },
      {
        code: '7211',
        label_en: 'Laundries – family & commercial',
        label_es: 'Lavanderías familiares y comerciales',
      },
      { code: '7216', label_en: 'Dry cleaners', label_es: 'Tintorerías' },
      {
        code: '7217',
        label_en: 'Carpet & upholstery cleaning',
        label_es: 'Limpieza de alfombras',
      },
      {
        code: '7230',
        label_en: 'Barber & beauty shops',
        label_es: 'Peluquerías y salones de belleza',
      },
      {
        code: '7251',
        label_en: 'Shoe repair & hat cleaning',
        label_es: 'Reparación de calzado',
      },
      {
        code: '7261',
        label_en: 'Funeral services & crematories',
        label_es: 'Servicios funerarios',
      },
      {
        code: '7277',
        label_en: 'Counseling services',
        label_es: 'Servicios de consejería',
      },
      {
        code: '7311',
        label_en: 'Advertising services',
        label_es: 'Servicios de publicidad',
      },
      {
        code: '7333',
        label_en: 'Commercial photography & graphic design',
        label_es: 'Fotografía comercial y diseño gráfico',
      },
      {
        code: '7338',
        label_en: 'Quick copy & reproduction',
        label_es: 'Copias e impresión rápida',
      },
      {
        code: '7339',
        label_en: 'Secretarial & support services',
        label_es: 'Servicios de secretariado',
      },
      {
        code: '7392',
        label_en: 'Management, consulting & PR',
        label_es: 'Consultoría y relaciones públicas',
      },
      {
        code: '7394',
        label_en: 'Equipment rental & leasing',
        label_es: 'Alquiler de equipos',
      },
      {
        code: '7399',
        label_en: 'Business services (other)',
        label_es: 'Otros servicios de negocios',
      },
      {
        code: '8111',
        label_en: 'Legal services & attorneys',
        label_es: 'Servicios legales y abogados',
      },
      { code: '8351', label_en: 'Child care services', label_es: 'Guarderías' },
      {
        code: '8398',
        label_en: 'Charitable & social service orgs',
        label_es: 'Organizaciones benéficas',
      },
      {
        code: '8699',
        label_en: 'Membership organizations (other)',
        label_es: 'Otras organizaciones de membresía',
      },
      {
        code: '8911',
        label_en: 'Architectural & engineering services',
        label_es: 'Servicios de arquitectura e ingeniería',
      },
      {
        code: '8931',
        label_en: 'Accounting & auditing',
        label_es: 'Contabilidad y auditoría',
      },
    ],
  },
];

export function getAllMccsForCategory(categoryId: string): string[] {
  const cat = MCC_CATEGORIES.find((c) => c.id === categoryId);
  return cat ? cat.subcategories.map((s) => s.code) : [];
}

export function getMccLabel(code: string, locale: 'en' | 'es' = 'en'): string {
  for (const cat of MCC_CATEGORIES) {
    const sub = cat.subcategories.find((s) => s.code === code);
    if (sub) return locale === 'es' ? sub.label_es : sub.label_en;
  }
  return code;
}
